import React from 'react';
import { Link } from 'react-router-dom';
import { 
  RiLockLine, 
  RiArrowLeftLine,
  RiInformationLine,
  RiDatabaseLine,
  RiSettings3Line,
  RiShieldCheckLine,
  RiShareLine,
  RiUserStarLine,
  RiFileSettingsLine,
  RiTimeLine,
  RiUserLine,
  RiFileEditLine,
  RiGlobalLine,
  RiExternalLinkLine
} from 'react-icons/ri';
import SEO from '../components/SEO';
import './Privacy.css';

const Privacy = () => {
  return (
    <>
      <SEO
        title="Privacy Policy - NEIR BTRC | Official Government Privacy & Data Protection"
        description="Read the Privacy Policy for the National Equipment Identity Register (NEIR) by Bangladesh Telecommunication Regulatory Commission (BTRC). Official government privacy policy covering data collection, storage, usage, and protection of personal information and IMEI data. Understand how your mobile device information is protected by the government mobile database system."
        keywords="Privacy Policy, NEIR BTRC Privacy, NEIR Bangladesh Privacy, NEIR.gov.bd Privacy, NEIR.btrc.gov.bd Privacy, neir.btrc.gov.bd Privacy, NEIR privacy policy, BTRC privacy policy, government privacy policy, official NEIR privacy, Bangladesh government privacy, mobile device data privacy, IMEI data protection, government mobile database privacy, official government privacy policy, BTRC data protection, NEIR data privacy, government telecommunications privacy, official mobile services privacy, Bangladesh telecom regulatory privacy, government mobile database privacy policy, official device registration privacy, government mobile verification privacy, official government portal privacy, BTRC data security, NEIR data security, government mobile services privacy policy, official mobile database privacy, government telecommunications privacy policy, Bangladesh mobile network privacy, official device verification privacy, government mobile registration privacy, official government services privacy, BTRC official privacy policy, NEIR official privacy policy, data protection Bangladesh, government data security, neir.btrc.gov.bd privacy, www.neir.btrc.gov.bd privacy, neir portal privacy, btrc neir portal privacy, neir btrc website privacy, neir btrc official privacy, neir btrc data protection, neir btrc data security, neir btrc bangladesh privacy, neir btrc gov privacy, neir btrc government privacy, .gov.bd privacy, gov.bd domain privacy, government domain Bangladesh privacy, official government domain privacy, btrc.gov.bd privacy, neir.btrc.gov.bd privacy, www.btrc.gov.bd privacy, www.neir.btrc.gov.bd privacy"
        ogTitle="Privacy Policy - NEIR BTRC | Official Government Privacy & Data Protection"
        ogDescription="Read the Privacy Policy for the National Equipment Identity Register (NEIR) by Bangladesh Telecommunication Regulatory Commission (BTRC)."
        ogImage="/images/btrc-logo.png"
        canonicalUrl="https://neir.btrc.gov.bd/privacy"
      />
      <div className="privacy-page">
      <div className="privacy-header">
        <div className="container">
          <Link to="/" className="back-link">
            <RiArrowLeftLine /> Back to Home
          </Link>
          <div className="privacy-title-section">
            <div className="privacy-icon">
              <RiLockLine />
            </div>
            <h1>Privacy Policy</h1>
            <p>Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>
        </div>
      </div>

      <div className="privacy-content">
        <div className="container">
          <div className="privacy-section">
            <h2>
              <RiInformationLine className="section-icon" />
              1. Introduction
            </h2>
            <p>
              The National Equipment Identity Register (NEIR) is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
              when you use our website and services.
            </p>
          </div>

          <div className="privacy-section">
            <h2>
              <RiDatabaseLine className="section-icon" />
              2. Information We Collect
            </h2>
            <p>We may collect the following types of information:</p>
            <ul>
              <li><strong>Personal Information:</strong> Name, email address, phone number, and other contact details</li>
              <li><strong>Device Information:</strong> IMEI numbers, device model, manufacturer, and registration details</li>
              <li><strong>Usage Data:</strong> Information about how you access and use our website and services</li>
              <li><strong>Technical Data:</strong> IP address, browser type, device information, and operating system</li>
            </ul>
          </div>

          <div className="privacy-section">
            <h2>
              <RiSettings3Line className="section-icon" />
              3. How We Use Your Information
            </h2>
            <p>We use the collected information for the following purposes:</p>
            <ul>
              <li>To provide and maintain our IMEI verification services</li>
              <li>To register and manage mobile devices in the NEIR database</li>
              <li>To prevent fraud, theft, and unauthorized use of mobile devices</li>
              <li>To comply with legal obligations and regulatory requirements</li>
              <li>To improve our services and user experience</li>
              <li>To communicate with you about your account and our services</li>
            </ul>
          </div>

          <div className="privacy-section">
            <h2>
              <RiShieldCheckLine className="section-icon" />
              4. Data Security
            </h2>
            <p>
              We implement appropriate technical and organizational security measures to protect your personal 
              information against unauthorized access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul>
              <li>Encryption of sensitive data in transit and at rest</li>
              <li>Regular security assessments and updates</li>
              <li>Access controls and authentication mechanisms</li>
              <li>Secure data storage and backup procedures</li>
            </ul>
          </div>

          <div className="privacy-section">
            <h2>
              <RiShareLine className="section-icon" />
              5. Data Sharing and Disclosure
            </h2>
            <p>
              We do not sell, trade, or rent your personal information to third parties. We may share your 
              information only in the following circumstances:
            </p>
            <ul>
              <li>With law enforcement agencies when required by law or to prevent illegal activities</li>
              <li>With mobile network operators for device verification and blocking purposes</li>
              <li>With authorized service providers who assist us in operating our services</li>
              <li>In case of a merger, acquisition, or sale of assets (with prior notice)</li>
            </ul>
          </div>

          <div className="privacy-section">
            <h2>
              <RiUserStarLine className="section-icon" />
              6. Your Rights
            </h2>
            <p>You have the following rights regarding your personal information:</p>
            <ul>
              <li><strong>Access:</strong> Request access to your personal data</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
              <li><strong>Deletion:</strong> Request deletion of your personal data (subject to legal requirements)</li>
              <li><strong>Objection:</strong> Object to processing of your personal data</li>
              <li><strong>Portability:</strong> Request transfer of your data to another service</li>
            </ul>
          </div>

          <div className="privacy-section">
            <h2>
              <RiFileSettingsLine className="section-icon" />
              7. Cookies and Tracking Technologies
            </h2>
            <p>
              We use cookies and similar tracking technologies to track activity on our website and store 
              certain information. You can instruct your browser to refuse all cookies or to indicate when 
              a cookie is being sent.
            </p>
          </div>

          <div className="privacy-section">
            <h2>
              <RiTimeLine className="section-icon" />
              8. Data Retention
            </h2>
            <p>
              We retain your personal information only for as long as necessary to fulfill the purposes outlined 
              in this Privacy Policy, unless a longer retention period is required or permitted by law.
            </p>
          </div>

          <div className="privacy-section">
            <h2>
              <RiUserLine className="section-icon" />
              9. Children's Privacy
            </h2>
            <p>
              Our services are not intended for individuals under the age of 18. We do not knowingly collect 
              personal information from children. If you become aware that a child has provided us with personal 
              information, please contact us immediately.
            </p>
          </div>

          <div className="privacy-section">
            <h2>
              <RiFileEditLine className="section-icon" />
              10. Changes to This Privacy Policy
            </h2>
            <p>
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting 
              the new Privacy Policy on this page and updating the "Last updated" date.
            </p>
          </div>

          <div className="privacy-section">
            <h2>
              <RiInformationLine className="section-icon" />
              11. Contact Us
            </h2>
            <p>
              If you have any questions about this Privacy Policy, please contact us through our 
              <Link to="/contact" className="inline-link"> Contact page</Link> or visit the official 
              BTRC website for more information.
            </p>
          </div>

          {/* Official Links Section */}
          <div className="official-links-section">
            <h2 className="official-links-title">
              <RiGlobalLine className="title-icon" />
              Important Official Links
            </h2>
            <div className="official-links-grid">
              <a 
                href="https://neir.btrc.gov.bd" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="official-link"
              >
                <RiGlobalLine className="link-icon" />
                <div className="link-content">
                  <span className="link-title">Official NEIR Portal</span>
                  <span className="link-url">neir.btrc.gov.bd</span>
                </div>
                <RiExternalLinkLine className="external-icon" />
              </a>
              
              <a 
                href="https://btrc.gov.bd" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="official-link"
              >
                <RiGlobalLine className="link-icon" />
                <div className="link-content">
                  <span className="link-title">BTRC Official Website</span>
                  <span className="link-url">btrc.gov.bd</span>
                </div>
                <RiExternalLinkLine className="external-icon" />
              </a>
              
              <a 
                href="https://btrc.gov.bd/imei-check" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="official-link"
              >
                <RiShieldCheckLine className="link-icon" />
                <div className="link-content">
                  <span className="link-title">IMEI Check Service</span>
                  <span className="link-url">Check Device Status</span>
                </div>
                <RiExternalLinkLine className="external-icon" />
              </a>
              
              <a 
                href="https://btrc.gov.bd/device-registration" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="official-link"
              >
                <RiDatabaseLine className="link-icon" />
                <div className="link-content">
                  <span className="link-title">Device Registration</span>
                  <span className="link-url">Register Your Device</span>
                </div>
                <RiExternalLinkLine className="external-icon" />
              </a>
              
              <a 
                href="https://btrc.gov.bd/complaints" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="official-link"
              >
                <RiInformationLine className="link-icon" />
                <div className="link-content">
                  <span className="link-title">File a Complaint</span>
                  <span className="link-url">Report Issues</span>
                </div>
                <RiExternalLinkLine className="external-icon" />
              </a>
              
              <Link to="/terms" className="official-link">
                <RiFileEditLine className="link-icon" />
                <div className="link-content">
                  <span className="link-title">Terms of Service</span>
                  <span className="link-url">View Terms</span>
                </div>
                <RiExternalLinkLine className="external-icon" />
              </Link>
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default Privacy;

